import React, { useState, useEffect } from 'react';
import {
  Typography,
  List,
  Button,
  Paper,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddUserDialog from './AddUserDialog';
import { getGroupsForUser } from '../apis/apis';

export default function MembersList({
  groupMembers,
  onMemberSelect,
  selectedMember,
  isCurrentPeriod,
  currentGroup,
  refreshGroups,
  compact = false
}) {
  const [openAddUser, setOpenAddUser] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Ensure we have group members before rendering
  if (!groupMembers || groupMembers.length === 0) {
    return (
      <Paper sx={{ p: 2, width: compact ? '100%' : '250px' }}>
        <Typography variant={compact ? "subtitle1" : "h6"} color="text.secondary">
          No group members found
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => setOpenAddUser(true)}
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
        >
          Add Member
        </Button>
        <AddUserDialog 
          open={openAddUser}
          onClose={() => setOpenAddUser(false)}
          groupId={currentGroup}
          onUserAdded={() => refreshGroups(currentGroup)}
        />
      </Paper>
    );
  }

  const handleUserAdded = async () => {
    try {
      await refreshGroups(currentGroup);
    } catch (error) {
      console.error('Error refreshing groups:', error);
    }
  };

  // Handle member click based on current period status
  const handleMemberClick = (member) => {
    if (!isCurrentPeriod) {
      onMemberSelect(member);
    }
    // Do nothing if it's the current period
  };

  // Determine styles based on compact mode
  const containerStyles = compact 
    ? { width: '100%', p: 0 } 
    : { 
        p: 2, 
        width: '100%', 
        maxWidth: '100%',
        maxHeight: 'calc(100vh - 120px)',
        overflow: 'auto'
      };

  return (
    <Paper sx={containerStyles} elevation={compact ? 0 : 1}>
      <Typography 
        variant={compact ? "subtitle1" : "h6"} 
        color="text.secondary" 
        sx={{ mb: 1, px: compact ? 1 : 0 }}
      >
        Group Members ({groupMembers.length})
      </Typography>
      <List dense disablePadding>
        {groupMembers.map((member) => {
          // Create the member item
          const memberItem = (
            <MenuItem
              key={member.id}
              selected={selectedMember?.id === member.id}
              onClick={() => handleMemberClick(member)}
              disabled={isCurrentPeriod}
              sx={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                py: 1,
                backgroundColor: selectedMember?.id === member.id ? 'primary.light' : 'transparent',
                opacity: isCurrentPeriod ? 0.7 : 1,
                cursor: isCurrentPeriod ? 'default' : 'pointer',
                '&.Mui-disabled': {
                  opacity: 1,
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  src={member?.profilePhotoUrl} 
                  sx={{ 
                    boxShadow: 2,
                    mr: 1, // Add margin to the right of the avatar
                    width: compact ? 28 : 32,
                    height: compact ? 28 : 32
                  }} 
                  alt={member.name}
                />
                <Typography variant={compact ? "body2" : "body1"}>{member.name}</Typography>
              </Box>
            </MenuItem>
          );

          // If it's the current period, wrap in a tooltip explaining why it's disabled
          return memberItem;
        })}
      </List>
      <Button
        startIcon={<AddIcon />}
        onClick={() => setOpenAddUser(true)}
        fullWidth
        variant="outlined"
        size="small"
        sx={{ mt: 2, mx: compact ? 1 : 0, width: compact ? 'calc(100% - 16px)' : '100%' }}
      >
        Add Member
      </Button>

      <AddUserDialog 
        open={openAddUser}
        onClose={() => setOpenAddUser(false)}
        groupId={currentGroup}
        onUserAdded={handleUserAdded}
      />
    </Paper>
  );
} 